import type { Actions } from "@/actions";
import type { RuntimeClient, RuntimeListener } from "@/clients/runtime.client";
import type { Telemetry } from "@/telemetry";

export type LifecycleDestination =
  | { page: "home" }
  | {
      page: "translate" | "summary" | "quiz" | "flashcard";
      transcriptId: string;
    };

export type LifecycleUi = {
  navigate: (destination: LifecycleDestination) => void;
  showPaywall: (utmSource: string) => void;
  checkout?: {
    idempotencyKey: string;
    isAuthenticated: boolean;
    customerEmail?: string;
    plan?: string;
    transactionId: string;
    transcriptId?: string;
    clearReturn: () => void;
    showSuccess: (onContinue: () => Promise<void>) => void;
    setSuccessLoading: () => void;
    showRetry: (onRetry: () => Promise<void>) => void;
    closeSuccess: () => void;
    onCompleted?: () => Promise<void>;
  };
};

export type LifecycleDependencies = {
  actions: Pick<Actions, "app" | "subscription" | "transcript">;
  runtime: RuntimeClient;
  telemetry: Pick<Telemetry, "error" | "trackPurchase">;
  checkoutStorage?: Pick<RuntimeClient["storage"], "getItem" | "setItem">;
};

// Releases before this one may have already scheduled feature-discovery and
// conversion notifications. Cancel their exact IDs without touching the
// functional recording notification range (12343-12345).
const RETIRED_FEATURE_NOTIFICATION_IDS = [
  13001, 13002, 13003, 13005, 13007, 13010, 13502, 13504, 13507, 13510,
  13514, 13901,
];

const _planPrices: Record<string, number> = {
  low_tier_weekly: 4.99,
  mid_tier_weekly: 7.99,
  top_tier_weekly: 9.99,
  low_tier_monthly: 14.99,
  mid_tier_monthly: 19.99,
  top_tier_monthly: 19.99,
  low_tier_annual: 39.99,
  mid_tier_annual: 49.99,
  top_tier_annual: 59.99,
};

export const checkoutTranscriptCompletionKey = (transcriptId: string) =>
  `stripe-checkout-transcript:${transcriptId}`;

export const createLifecycle = (dependencies: LifecycleDependencies) => {
  const checkoutStorage =
    dependencies.checkoutStorage || dependencies.runtime.storage;

  return {
    attach: (ui: LifecycleUi): (() => void) => {
      let disposed = false;
      const listeners = new Set<RuntimeListener>();

      const register = (
        operation: string,
        pending: Promise<RuntimeListener>,
      ) => {
        void pending
          .then((listener) => {
            if (disposed) void listener.remove().catch(() => {});
            else listeners.add(listener);
          })
          .catch(() => {
            dependencies.telemetry.error("lifecycle.listener", { operation });
          });
      };

      dependencies.actions.app.recordOpen("launch");

      if (ui.checkout) {
        const checkout = ui.checkout;
        const trackPurchase = async () => {
          const trackingKey = `stripe-checkout-purchase:${checkout.idempotencyKey}`;
          let alreadyTracked = false;
          try {
            alreadyTracked =
              (await checkoutStorage.getItem(trackingKey)) === "true";
          } catch {
            dependencies.telemetry.error("lifecycle.checkoutStorage", {
              operation: "read",
            });
          }
          if (disposed || alreadyTracked) return;
          dependencies.telemetry.trackPurchase(
            {
              plan: checkout.plan,
              currency: "USD",
              value: checkout.plan ? _planPrices[checkout.plan] || 4.99 : 4.99,
              transactionId: checkout.transactionId,
            },
            { email: checkout.customerEmail },
          );
          try {
            await checkoutStorage.setItem(trackingKey, "true");
          } catch {
            dependencies.telemetry.error("lifecycle.checkoutStorage", {
              operation: "write",
            });
          }
        };

        if (!checkout.isAuthenticated) {
          checkout.clearReturn();
        } else {
          const continueCheckout = async () => {
            if (disposed) return;
            checkout.setSuccessLoading();
            const result =
              await dependencies.actions.subscription.completeCheckout({
                transcriptId: checkout.transcriptId,
              });
            if (disposed) return;
            if ("error" in result || !result.value.isSubscribed) {
              checkout.showRetry(continueCheckout);
              return;
            }

            if (
              result.value.transcriptionStarted &&
              result.value.transcriptId
            ) {
              try {
                await checkoutStorage.setItem(
                  checkoutTranscriptCompletionKey(result.value.transcriptId),
                  "true",
                );
              } catch {
                dependencies.telemetry.error("lifecycle.checkoutStorage", {
                  operation: "writeTranscriptCompletion",
                });
              }
            }
            await trackPurchase();
            if (disposed) return;
            checkout.clearReturn();
            checkout.closeSuccess();
            await checkout.onCompleted?.();
          };

          checkout.showSuccess(continueCheckout);
          void continueCheckout();
        }
      }

      if (dependencies.runtime.isNative) {
        void dependencies.runtime.notifications
          .cancel(RETIRED_FEATURE_NOTIFICATION_IDS)
          .catch(() => {
            dependencies.telemetry.error(
              "lifecycle.retiredFeatureNotifications",
              { operation: "cancel" },
            );
          });
        register(
          "appStateChange",
          dependencies.runtime.app.onStateChange(({ isActive }) => {
            if (!isActive || disposed) return;
            dependencies.actions.app.recordOpen("foreground");
          }),
        );
        register(
          "appUrlOpen",
          dependencies.runtime.app.onUrlOpen(({ url }) => {
            if (disposed) return;
            try {
              const parsed = new URL(url);
              if (parsed.searchParams.get("deepLink") === "paywall") {
                ui.showPaywall(
                  parsed.searchParams.get("utm_source") || "app-url",
                );
              }
            } catch {
              // An unrelated or malformed OS URL is not a product failure.
            }
          }),
        );
      }

      return () => {
        if (disposed) return;
        disposed = true;
        for (const listener of listeners)
          void listener.remove().catch(() => {});
        listeners.clear();
      };
    },
  };
};

export type Lifecycle = ReturnType<typeof createLifecycle>;
